import React, { ReactElement, useCallback, useMemo } from 'react';
import { formatTranslation, userRoleLabelKey, unsafeTranslationKey } from 'intl';
import { WorkflowTranslationKeys } from 'intl/keys';
import Box from '@mui/material/Box';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useIntl } from 'react-intl';
import { compareObjectIds } from 'shared/utils/id/compare';
import {
  hasApprovalRequired,
  isParallelApproval,
  isSequentialApproval,
} from 'shared/domain/workflow/types';
import { ApproverRow } from './presentational';
import type { ApproversListWithDragDropProps } from './types';
import {
  sectionTitleSx,
  infoAlertSx,
  autocompleteListboxSx,
  getOptionSx,
  getOptionTextSx,
} from './styles';

export type { ApproversListWithDragDropProps } from './types';

export function ApproversListWithDragDrop({
  availableApprovers,
  value,
  approvers,
  onChange,
  approvalType,
  disabled = false,
  isLocked = false,
}: ApproversListWithDragDropProps): ReactElement {
  const intl = useIntl();
  const approvalOrderInfoMessageId =
    approvalType == null
      ? null
      : isParallelApproval({ approvalType })
        ? 'workflow_configuration_approvers_order_parallel_info'
        : isSequentialApproval({ approvalType })
          ? 'workflow_configuration_approvers_order_sequential_info'
          : null;

  const selectedUsers = approvers ?? value ?? [];
  const isLastApprover = selectedUsers.length === 1;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const userIds = useMemo(() => selectedUsers.map((u) => u._id), [selectedUsers]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = selectedUsers.findIndex((u) => compareObjectIds(u._id, active.id));
      const newIndex = selectedUsers.findIndex((u) => compareObjectIds(u._id, over.id));

      if (oldIndex === -1 || newIndex === -1) return;

      onChange(arrayMove(selectedUsers, oldIndex, newIndex));
    },
    [selectedUsers, onChange]
  );

  const handleRemove = useCallback(
    (userId: string) => {
      onChange(selectedUsers.filter((u) => !compareObjectIds(u._id, userId)));
    },
    [selectedUsers, onChange]
  );

  return (
    <Stack spacing={2}>
      {selectedUsers.length > 0 && (
        <Box>
          <Typography variant="body2" color="text.primary" sx={sectionTitleSx}>
            {formatTranslation(intl, WorkflowTranslationKeys.configurationStageApproversLabel)}
          </Typography>
          {approvalType && hasApprovalRequired({ approvalType }) && approvalOrderInfoMessageId && (
            <Alert severity="info" sx={infoAlertSx}>
              {formatTranslation(intl, unsafeTranslationKey(approvalOrderInfoMessageId) )}
            </Alert>
          )}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={userIds} strategy={verticalListSortingStrategy}>
              {selectedUsers.map((approver, index) => (
                <ApproverRow
                  key={approver._id}
                  approver={approver}
                  index={index}
                  onDelete={() => handleRemove(approver._id)}
                  disabled={disabled}
                  deleteDisabled={isLocked && isLastApprover}
                />
              ))}
            </SortableContext>
          </DndContext>
        </Box>
      )}

      <Autocomplete
        multiple
        disableClearable
        disableCloseOnSelect
        filterSelectedOptions
        fullWidth
        size="small"
        options={availableApprovers}
        getOptionLabel={(u) => u.label}
        value={selectedUsers}
        onChange={(_, newValue) => onChange(newValue)}
        disabled={disabled}
        renderTags={() => null} // Tags rendered separately as draggable ApproverRow
        ListboxProps={{ sx: autocompleteListboxSx }}
        renderOption={(props, option) => {
          const isSelected = selectedUsers.some((u) => compareObjectIds(u._id, option._id));
          return (
            <li {...props} key={option._id} style={getOptionSx(isSelected)}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ width: '100%' }}
              >
                <Typography variant="body2" sx={getOptionTextSx(isSelected)}>
                  {option.label}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.primary"
                  sx={{ flexShrink: 0, fontWeight: 400 }}
                >
                  {formatTranslation(intl, userRoleLabelKey(option.role))}
                </Typography>
              </Stack>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={formatTranslation(intl, WorkflowTranslationKeys.configurationStageAddApproverLabel,)}
            placeholder={formatTranslation(intl, WorkflowTranslationKeys.configurationStageAddApproverPlaceholder,)}
            size="small"
          />
        )}
        data-qa="workflow-stage-form-add-approver"
      />
    </Stack>
  );
}
